import { EstadoStock, ErrorBodega, PropietarioRef, TipoPrestamo } from './bodega.model';

// Las operaciones de bodega (llegada de stock, devolución y salida) y el historial por operaciones: el contrato del Anexo H del plan.
// Cada una se guarda en UNA petición y UNA transacción: o se guarda completa o no se guarda nada.

export type TipoOperacion = 'llegada_stock' | 'devolucion' | 'salida';

/** Cómo vuelve una herramienta o una parte de un préstamo por cantidad. */
export type EstadoDeVuelta = 'buen_estado' | 'con_dano';

// ── Lo que se envía ──────────────────────────────────────────────────────────────────────

/** Material por cantidad (un consumible o una herramienta controlada por cantidad). */
export interface LineaLlegadaMaterial {
  tipo: 'material';
  productoId: number;
  cantidad: number;
  ubicacion?: string;
}

/**
 * Piezas NUEVAS de un producto por unidad: o `cantidad` (el sistema genera los códigos al confirmar) o `codigos`, nunca los dos. Los códigos
 * son de piezas que ya vienen etiquetadas, o los que el sistema generó antes y dejó guardados (el servidor lo reconoce código por código y
 * crea las piezas con exactamente esos, que pasan a «ingresado»).
 */
export interface LineaLlegadaPieza {
  tipo: 'pieza';
  productoId: number;
  cantidad?: number;
  codigos?: string[];
  ubicacion?: string;
}

export type LineaDeLlegada = LineaLlegadaMaterial | LineaLlegadaPieza;

export interface LlegadaNueva {
  claveCliente: string;
  /** Quién trae la mercadería (obligatorio, 1 a 120 caracteres). */
  proveedor: string;
  /** Hasta 20, de 1 a 40 caracteres cada una, sin repetir (sin distinguir mayúsculas ni tildes). */
  facturas?: string[];
  descripcion?: string;
  lineas: LineaDeLlegada[];
}

/** A qué se imputa un consumo: una partida del presupuesto de Trabajo o una categoría del Gasto General de Trabajo de la obra. */
export interface ImputacionNueva {
  tipo: 'partida' | 'gasto_general';
  id: number;
}

/**
 * Material que sale (solo consumibles, sin ubicación). `propietarioId` = la empresa dueña de la línea de la que se retira (omitir = el stock
 * propio de la obra); si es de otra empresa la `imputacion` es obligatoria (con stock propio es opcional).
 */
export interface LineaSalidaMaterial {
  tipo: 'material';
  productoId: number;
  cantidad: number;
  propietarioId?: number;
  imputacion?: ImputacionNueva;
}

/** Una herramienta que sale en préstamo: por unidad (`codigo`) o por cantidad (`productoId` y `cantidad`). */
export interface LineaSalidaPrestamo {
  tipo: 'prestamo';
  plazo: TipoPrestamo;
  /** Solo en los indefinidos (AAAA-MM-DD, no anterior a hoy). */
  fechaEstimada?: string;
  codigo?: string;
  productoId?: number;
  cantidad?: number;
}

export type LineaDeSalida = LineaSalidaMaterial | LineaSalidaPrestamo;

export interface SalidaNueva {
  claveCliente: string;
  /** Quién retira, persona o cuadrilla (obligatorio, 1 a 120 caracteres). */
  retira: string;
  lineas: LineaDeSalida[];
}

export interface LineaDevolucionMaterial {
  tipo: 'material';
  productoId: number;
  cantidad: number;
  ubicacion?: string;
  /** La empresa dueña de la línea que repone (omitir = el stock propio); si es de otra empresa la imputación es obligatoria. */
  propietarioId?: number;
  imputacion?: ImputacionNueva;
}

export interface LineaDevolucionHerramienta {
  tipo: 'herramienta';
  codigo: string;
  estado: EstadoDeVuelta;
  nota?: string;
}

export interface LineaDevolucionPrestamo {
  tipo: 'prestamo';
  lineaId: number;
  cantidad: number;
  nota?: string;
}

export type LineaDeDevolucion = LineaDevolucionMaterial | LineaDevolucionHerramienta | LineaDevolucionPrestamo;

export interface DevolucionNueva {
  claveCliente: string;
  /** Obligatorio si hay material (1 a 120); sin material el servidor lo calcula y lo que se mande se ignora. */
  devuelve?: string;
  lineas: LineaDeDevolucion[];
}

// ── Lo que se responde ───────────────────────────────────────────────────────────────────

export interface UsuarioDeOperacion {
  id: number;
  nombre: string;
  correo?: string;
}

export interface ProductoDeLinea {
  id: number;
  nombre: string;
  /** La unidad de medida (und, kg, saco…). */
  medida: string;
  ean: string | null;
}

/** El préstamo de una línea: el que creó una salida, o aquel del que vuelve algo en una devolución (a quién se había prestado). */
export interface PrestamoDeLinea {
  id: number;
  receptor: string;
  plazo: TipoPrestamo;
  fechaEntrega: string;
  vencimiento: string;
}

/** Una imputación de una línea de material guardada (el consumo repartido entre partidas trae una por partida). */
export interface ImputacionGuardada {
  tipo: 'partida' | 'gasto_general';
  codigo: string;
  nombre: string;
  cantidad: number;
}

export interface LineaDeOperacion {
  /** material · pieza (nueva, de una llegada) · herramienta (vuelve una pieza) · prestamo (sale en préstamo o vuelve parte de uno por cantidad). */
  tipo: 'material' | 'pieza' | 'herramienta' | 'prestamo';
  producto: ProductoDeLinea;
  cantidad: number;
  codigo: string | null;
  ubicacion: string | null;
  estado: EstadoDeVuelta | null;
  nota: string | null;
  prestamo: PrestamoDeLinea | null;
  /** Solo en una línea de material de OTRA empresa. */
  propietario?: PropietarioRef;
  /** Solo en una línea de material que se imputó. */
  imputaciones?: ImputacionGuardada[];
}

/** Una operación guardada: lo mismo en la respuesta de los POST y en las filas del historial. */
export interface Operacion {
  clase: 'operacion';
  id: number;
  tipo: TipoOperacion;
  creadoEn: string;
  usuario: UsuarioDeOperacion;
  obra: { id: number; nombre: string };
  dia: { id: number; fecha: string };
  /** Salida: quien retira. Devolución: quien devuelve. Llegada: null. */
  persona: string | null;
  proveedor: string | null;
  facturas: string[];
  descripcion: string | null;
  lineas: LineaDeOperacion[];
}

export interface RespuestaOperacion {
  operacion: Operacion;
  /** El stock actual de los productos por cantidad que tocó la operación. */
  saldos: EstadoStock[];
  /** true = la clave ya se había guardado y el servidor devolvió la misma operación. */
  repetido: boolean;
}

// ── Errores de una operación ─────────────────────────────────────────────────────────────

/** Un problema que encontró el servidor, con la posición (desde 0) de la línea que lo causó (null si es general) y sus datos de apoyo. */
export interface ProblemaDeOperacion {
  codigo: string;
  mensaje: string;
  linea: number | null;
  /** Lo demás que trae el problema (productoId, nombre, disponible, solicitado, codigo, estado, campo, pendiente…). */
  datos: Record<string, unknown>;
}

/**
 * 400, 404 y 409 de una operación: NO se guardó nada. `linea` y `problemas` dicen qué línea falló y por qué, para marcarla en el carrito sin
 * perder el resto. `codigo` es el del primer problema.
 */
export class ErrorOperacion extends ErrorBodega {
  constructor(
    status: number,
    mensaje: string,
    readonly codigo: string,
    readonly linea: number | null,
    readonly problemas: ProblemaDeOperacion[],
  ) {
    super(status, mensaje);
    this.name = 'ErrorOperacion';
  }
}

// ── Historial por operaciones ────────────────────────────────────────────────────────────

/** Los tipos de fila del historial: las tres operaciones y lo registrado suelto (con el prefijo de su clase, porque los nombres se repiten). */
export type TipoHistorial =
  | TipoOperacion
  | 'movimiento_entrada' | 'movimiento_salida' | 'movimiento_ajuste'
  | 'herramienta_alta' | 'herramienta_entrega' | 'herramienta_devolucion' | 'herramienta_mantencion'
  | 'herramienta_reactivacion' | 'herramienta_baja' | 'herramienta_ubicacion' | 'herramienta_aplazamiento' | 'herramienta_traspaso'
  | 'dia_apertura' | 'dia_cierre' | 'dia_reapertura';

export const TIPOS_DE_HISTORIAL: readonly TipoHistorial[] = [
  'llegada_stock', 'devolucion', 'salida',
  'movimiento_entrada', 'movimiento_salida', 'movimiento_ajuste',
  'herramienta_alta', 'herramienta_entrega', 'herramienta_devolucion', 'herramienta_mantencion',
  'herramienta_reactivacion', 'herramienta_baja', 'herramienta_ubicacion', 'herramienta_aplazamiento', 'herramienta_traspaso',
  'dia_apertura', 'dia_cierre', 'dia_reapertura',
];

export interface UsuarioDeFila {
  id: number;
  nombre: string;
}

interface FilaBase {
  id: number;
  creadoEn: string;
  usuario: UsuarioDeFila;
}

/** Un movimiento de stock registrado suelto (anterior al rediseño, un ajuste, o una entrada o salida por las rutas de siempre). */
export interface FilaMovimiento extends FilaBase {
  clase: 'movimiento';
  tipo: 'movimiento_entrada' | 'movimiento_salida' | 'movimiento_ajuste';
  origen: 'manual' | 'carga_inicial' | 'prestamo' | 'traspaso' | string;
  receptor: string | null;
  descripcion: string | null;
  documento: string | null;
  motivo: string | null;
  lineas: { productoId: number; nombre: string; unidad: string; delta: number; saldoPosterior: number; ubicacion: string | null }[];
  /** De quién es el stock que se movió, si es de otra empresa. */
  propietario?: PropietarioRef | null;
  /** Si el movimiento es el despacho o la recepción de un traspaso (origen «traspaso»). */
  traspaso?: { id: number; codigo: string } | null;
}

/** Un evento suelto de una pieza o de un préstamo. */
export interface FilaHerramienta extends FilaBase {
  clase: 'herramienta';
  tipo: Extract<TipoHistorial, `herramienta_${string}`>;
  productoId: number | null;
  unidadId: number | null;
  prestamoId: number | null;
  estadoAnterior: string | null;
  estadoNuevo: string | null;
  nota: string | null;
  cantidad: number | null;
  valorAnterior: string | null;
  valorNuevo: string | null;
  fechaAnterior: string | null;
  fechaNueva: string | null;
  producto: { id: number; nombre: string } | null;
  unidad: { id: number; codigo: string } | null;
  prestamo: { id: number; receptor: string } | null;
  /** El traspaso de un evento «herramienta_traspaso», si el servidor lo dice. */
  traspaso?: { id: number; codigo: string } | null;
  propietario?: PropietarioRef | null;
}

/** La apertura, el cierre o la reapertura de un día de bodega. */
export interface FilaDia extends FilaBase {
  clase: 'dia';
  tipo: 'dia_apertura' | 'dia_cierre' | 'dia_reapertura';
  dia: { id: number; fecha: string };
  /** La nota del cierre o el motivo de la reapertura. */
  nota: string | null;
  /** En un cierre, el resumen del día; en los demás, null. */
  resumen: unknown | null;
}

export type FilaHistorial = Operacion | FilaMovimiento | FilaHerramienta | FilaDia;

export interface PaginaHistorial {
  items: FilaHistorial[];
  total: number;
  page: number;
  pageSize: number;
  /** 0 si no hay nada. */
  totalPaginas: number;
}

export type TamanoDePaginaHistorial = 25 | 50 | 100;

/** Los filtros de GET /historial (todos opcionales; `fecha` no se combina con `desde` y `hasta`). */
export interface FiltrosDeHistorial {
  page?: number;
  pageSize?: TamanoDePaginaHistorial;
  fecha?: string;
  desde?: string;
  hasta?: string;
  tipo?: TipoHistorial;
  usuarioId?: number;
  productoId?: number;
  /** Texto libre (hasta 100 caracteres): producto, código de pieza, persona, proveedor y factura. */
  q?: string;
}

export interface UsuarioDeHistorial {
  id: number;
  nombre: string;
}
